import { Controller } from "@hotwired/stimulus"

// Subscription settings modal.
//
// The toggles are real checkboxes/radios kept visually hidden, so the form
// posts normally and works without JS; this controller only paints the on/off
// skin and keeps the "adds N sessions" line honest as the selection changes.
export default class extends Controller {
  static targets = [
    "scrim",
    "panel",
    "kindLabel",
    "kindBox",
    "reminderLabel",
    "summary",
    "confirm",
  ]
  static values = { sessionCounts: Object }

  connect() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.paint()
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  open() {
    this.scrimTarget.classList.remove("hidden")
    this.panelTarget.classList.remove("hidden")
    document.addEventListener("keydown", this.closeOnEscape)
  }

  close() {
    this.scrimTarget.classList.add("hidden")
    this.panelTarget.classList.add("hidden")
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }

  confirmUnsubscribe() {
    this.confirmTarget?.showModal()
  }

  toggleKind() {
    this.paint()
  }

  toggleReminder() {
    this.paint()
  }

  paint() {
    this.kindLabelTargets.forEach((label) => {
      const input = label.querySelector("input")
      this.skinToggle(label, input.checked)
      this.skinBox(
        label.querySelector("[data-racing-subscription-target='kindBox']"),
        input.checked,
      )
    })

    this.reminderLabelTargets.forEach((label) => {
      const input = label.querySelector("input")
      this.skinToggle(label, input.checked)
    })

    this.updateSummary()
  }

  skinToggle(label, on) {
    label.style.background = on
      ? "color-mix(in srgb, var(--color-vivid-purple) 10%, transparent)"
      : "transparent"
    label.style.boxShadow = on
      ? "inset 0 0 0 1px color-mix(in srgb, var(--color-vivid-purple) 34%, transparent)"
      : "inset 0 0 0 1px var(--app-border-strong)"
    label.style.color = on ? "var(--app-text)" : "var(--app-text-secondary)"
  }

  skinBox(box, on) {
    if (!box) return
    box.style.background = on ? "var(--color-vivid-purple)" : "transparent"
    box.style.boxShadow = on
      ? "none"
      : "inset 0 0 0 1px var(--app-border-strong)"
    box.querySelector("svg").style.visibility = on ? "visible" : "hidden"
  }

  // An empty selection means *every* kind: the same rule the server applies:
  // so the copy has to say that rather than "0 sessions".
  updateSummary() {
    if (!this.hasSummaryTarget) return

    const counts = this.sessionCountsValue || {}
    const rounds = counts.__rounds || 0
    const checked = this.kindLabelTargets
      .map((l) => l.querySelector("input"))
      .filter((i) => i.checked)
      .map((i) => i.value)

    // `__rounds` shares the counts object but is not a session kind; summing it
    // into the total reported every season as twice its real session count.
    const total =
      checked.length === 0
        ? Object.entries(counts)
            .filter(([kind]) => kind !== "__rounds")
            .reduce((sum, [, n]) => sum + n, 0)
        : checked.reduce((sum, kind) => sum + (counts[kind] || 0), 0)

    const suffix =
      checked.length === 0 ? " Empty selection means every session." : ""
    this.summaryTarget.textContent = `Adds ${total} session${total === 1 ? "" : "s"} across ${rounds} round${rounds === 1 ? "" : "s"} this season.${suffix}`
  }
}
